import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  User,
  Building,
  Phone,
  Wifi,
  MapPin,
  CheckCircle2,
  X,
  Edit3,
  Send,
  RefreshCw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CalendarClock,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@hts/ui";
import { useGet, apiPost, apiPatch } from "../../hooks/useApi.js";
import {
  StatusBadge,
  PriorityBadge,
  SlaChip,
  SentimentChip,
} from "../../components/ui/StatusBadge.js";
import { CardSkeleton } from "../../components/ui/Skeleton.js";
import { relativeTime, formatHours, formatDate } from "../../lib/format.js";
import { TimeDraftsReview } from "./TimeDraftsReview.js";
import { SimilarTickets } from "./SimilarTickets.js";
import { TicketScriptLibraryCard } from "./TicketScriptLibraryCard.js";
import { sanitizeHtml } from "../../lib/sanitizeHtml.js";
import { NoteContent } from "../../components/NoteContent.js";
import { collapseDuplicateNotes } from "../../lib/collapseDuplicateNotes.js";
import type {
  AppointmentSlot,
  TechnicianAvailability,
  Ticket,
  TicketNote,
  Technician,
} from "../../api/types.js";

const WORK_MODE_ICONS = { onsite: MapPin, remote: Wifi, phone: Phone };

interface SchedulingSlots {
  durationMinutes: number;
  technicians: Array<{
    technicianId: string;
    name: string;
    state: "available" | "busy" | "off_hours" | "not_on_call" | "capacity" | "pto";
    slots: Array<{ startsAt: string; endsAt: string }>;
  }>;
}

interface TicketStatuses {
  current: string | null;
  writable: boolean;
  reason?: string;
  items: Array<{ id: number; name: string }>;
}

interface TriageResult {
  id: string;
  kind: string | null;
  confidence: number;
  reason: string;
  output: Record<string, unknown> | null;
  board?: string | null;
  type?: string | null;
  subtype?: string | null;
  item?: string | null;
  priority?: string | null;
  applied: boolean;
  correction?: { verdict?: "accepted" | "overridden"; reason?: string | null } | null;
}

/** Inline evidence detail attached to each actionable suggestion (Part 1). */
interface SuggestionDetailDto {
  before?: string | null;
  after?: string | null;
  scoreDetail?: {
    skill?: number;
    availability?: number;
    workload?: number;
    familiarity?: number;
    urgency?: number;
  };
  alternates?: Array<{ label: string; why: string }>;
  topMatches?: Array<{ cwTicketId: number; title: string }>;
}

interface SuggestionItemDto {
  key: string;
  section: string;
  action: string;
  label: string;
  confidence: number;
  reason: string;
  available: boolean;
  computedAt?: string;
  payload: Record<string, unknown>;
  detail?: SuggestionDetailDto;
}

/** Ticket evidence reused by the analysis card (Part 2 deeper analytics). */
interface SuggestionsEvidenceDto {
  entities?: string[];
  timeBySource?: Array<{ label: string; hours: number; count: number }>;
  latestWorkNote?: string | null;
  candidates?: Array<{
    technicianId: string;
    technicianName: string;
    score: number | null;
    skillScore?: number;
    availabilityScore?: number;
    workloadScore?: number;
    familiarityScore?: number;
    urgencyScore?: number;
    reason?: string | null;
  }>;
}

interface SuggestionsData {
  items: SuggestionItemDto[];
  outcomes: Array<{ suggestionKey: string; status: string; at: string }>;
  evidence?: SuggestionsEvidenceDto;
}

// ---------------------------------------------------------------------------
// Prev/next ticket navigation + note drafts (sessionStorage)
// ---------------------------------------------------------------------------

const TICKET_NAV_KEY = "tsc:ticketNav";
const NOTE_DRAFT_PREFIX = "tsc:noteDraft:";
/** List contexts older than this are ignored (stale tab / old session). */
const TICKET_NAV_MAX_AGE_MS = 30 * 60 * 1000;

interface TicketNavContext {
  ids: string[];
  at: number;
}

function readTicketNav(): TicketNavContext | null {
  try {
    const raw = sessionStorage.getItem(TICKET_NAV_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TicketNavContext> | null;
    if (!parsed || !Array.isArray(parsed.ids)) return null;
    const ids = parsed.ids.filter((x): x is string => typeof x === "string");
    if (ids.length === 0) return null;
    const at = typeof parsed.at === "number" ? parsed.at : 0;
    if (Date.now() - at > TICKET_NAV_MAX_AGE_MS) return null;
    return { ids, at };
  } catch {
    return null;
  }
}

function readNoteDraft(ticketId: string | undefined): string {
  if (!ticketId) return "";
  try {
    return sessionStorage.getItem(NOTE_DRAFT_PREFIX + ticketId) ?? "";
  } catch {
    return "";
  }
}

function writeNoteDraft(ticketId: string | undefined, text: string): void {
  if (!ticketId) return;
  try {
    if (text.trim()) sessionStorage.setItem(NOTE_DRAFT_PREFIX + ticketId, text);
    else sessionStorage.removeItem(NOTE_DRAFT_PREFIX + ticketId);
  } catch {
    /* storage unavailable (private mode) — drafts are best-effort */
  }
}

export function TicketDetailPage({ embeddedId, part }: { embeddedId?: string; part?: "main" | "sidebar" } = {}) {
  const routeParams = useParams<{ id: string }>();
  const id = embeddedId ?? routeParams.id;
  const navigate = useNavigate();

  // Prev/next navigation through the ticket list the user came from
  // (TicketsPage stashes the filtered+sorted id order in sessionStorage).
  // Read once per mount; the list order does not change while paging
  // through tickets. Hidden when there is no (fresh) context or the ticket
  // is not part of it.
  const ticketNav = React.useMemo(readTicketNav, []);
  const navIdx = id && ticketNav ? ticketNav.ids.indexOf(id) : -1;
  const prevTicketId = ticketNav && navIdx > 0 ? ticketNav.ids[navIdx - 1] : null;
  const nextTicketId =
    ticketNav && navIdx >= 0 && navIdx < ticketNav.ids.length - 1
      ? ticketNav.ids[navIdx + 1]
      : null;
  const {
    data: ticket,
    loading: ticketLoading,
    refetch: refetchTicket,
  } = useGet<Ticket>(id ? `/api/v1/tickets/${id}` : null);
  const {
    data: notesData,
    loading: notesLoading,
    refetch: refetchNotes,
  } = useGet<{ items: TicketNote[] }>(id ? `/api/v1/tickets/${id}/notes` : null);
  const {
    data: analysis,
    loading: analysisLoading,
    refetch: refetchAnalysis,
  } = useGet<any>(id ? `/api/v1/tickets/${id}/analysis` : null);
  const {
    data: triageResults,
    loading: triageLoading,
    refetch: refetchTriage,
  } = useGet<TriageResult[]>(ticket?.cwId ? `/api/v1/tickets/${ticket.cwId}/triage` : null);
  const {
    data: suggestionsData,
    loading: suggestionsLoading,
    refetch: refetchSuggestions,
  } = useGet<SuggestionsData>(id ? `/api/v1/tickets/${id}/suggestions` : null);
  const [analyzing, setAnalyzing] = React.useState(false);
  const [noteText, setNoteText] = React.useState(() => readNoteDraft(id));
  const [noteInternal, setNoteInternal] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  // In-progress note drafts survive navigation (prev/next, accidental
  // back-click): keyed by ticket id in sessionStorage; cleared on submit
  // (submitNote empties noteText, which removes the draft below).
  // The persist effect runs BEFORE the load effect in each commit, and the
  // ref guard makes it skip the commit right after an id change, so the old
  // ticket's text is never written over the new ticket's draft.
  const noteDraftIdRef = React.useRef(id);
  React.useEffect(() => {
    if (noteDraftIdRef.current === id) writeNoteDraft(id, noteText);
  }, [id, noteText]);
  React.useEffect(() => {
    if (noteDraftIdRef.current !== id) {
      noteDraftIdRef.current = id;
      setNoteText(readNoteDraft(id));
    }
  }, [id]);
  const [acceptedSuggestions, setAcceptedSuggestions] = React.useState<Set<string>>(new Set());
  const [overriddenSuggestions, setOverriddenSuggestions] = React.useState<Set<string>>(new Set());
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Quick-action state
  const [dialog, setDialog] = React.useState<"status" | "assign" | "time" | "escalate" | "booking" | "note" | null>(null);
  const [noteToRecipient, setNoteToRecipient] = React.useState(false);
  const [noteToResources, setNoteToResources] = React.useState(false);
  const [noteToEmails, setNoteToEmails] = React.useState("");
  const [noteSms, setNoteSms] = React.useState(false);
  const [typeSel, setTypeSel] = React.useState("");
  const [subtypeSel, setSubtypeSel] = React.useState("");
  const [itemSel, setItemSel] = React.useState("");
  const { data: catOptions } = useGet<{ types: string[]; subtypes: string[]; items: string[] }>("/api/v1/tickets/type-options");
  React.useEffect(() => {
    setTypeSel(ticket?.typeName ?? "");
    setSubtypeSel(ticket?.subtypeName ?? "");
    setItemSel(ticket?.itemName ?? "");
  }, [ticket?.typeName, ticket?.subtypeName, ticket?.itemName]);
  const [actionBusy, setActionBusy] = React.useState(false);
  const [statusId, setStatusId] = React.useState<number | null>(null);
  const [techId, setTechId] = React.useState<string | null>(null);
  const [timeHours, setTimeHours] = React.useState("");
  const [timeStart, setTimeStart] = React.useState('');
  const [timeEnd, setTimeEnd] = React.useState('');
  const [timeBillable, setTimeBillable] = React.useState<"Billable" | "DoNotBill">("DoNotBill");
  const [selectedPhone, setSelectedPhone] = React.useState<string>("");
  const phoneOptions = (ticket?.contactPhones ?? []).filter((p) => !/email/i.test(p.label) && p.value);
  React.useEffect(() => {
    const company = phoneOptions.find((p) => /^company$/i.test(p.label));
    if (company?.value) setSelectedPhone(company.value);
    else if (phoneOptions.length === 1) setSelectedPhone(phoneOptions[0]?.value ?? "");
    else setSelectedPhone("");
  }, [ticket?.id, phoneOptions.length]);
  const [timeWorkTypeId, setTimeWorkTypeId] = React.useState<number | null>(null);
  const { data: timeTypesData } = useGet<{ items?: Array<{ id: number; name: string }> } | Array<{ id: number; name: string }>>(dialog === "time" ? "/api/v1/tickets/time-types" : null);
  const timeTypes = Array.isArray(timeTypesData) ? timeTypesData : (timeTypesData?.items ?? []);
  const [timeNotes, setTimeNotes] = React.useState("");
  const [escalateReason, setEscalateReason] = React.useState("");
  const [escalateTechId, setEscalateTechId] = React.useState<string | null>(null);
  const [bookingUrl, setBookingUrl] = React.useState<string | null>(null);
  const [bookingError, setBookingError] = React.useState("");

  const statuses = useGet<TicketStatuses>(
    id ? `/api/v1/tickets/${id}/statuses` : null,
  );
  const technicians = useGet<{ items: Technician[] }>(
    dialog === "assign" ? "/api/v1/technicians" : null,
  );
  const scheduling = useGet<SchedulingSlots>(
    dialog === "assign" && ticket
      ? `/api/v1/schedule/slots?board=${encodeURIComponent(ticket.board)}&limit=1`
      : null,
  );
  // #99: ranked dispatch candidates for this ticket
  const recommend = useGet<{ candidates: Array<{ techId: string; techName: string; score: number; rank: number; reason: string; skillScore: number; availabilityScore: number; workloadScore: number; familiarityScore: number; urgencyScore: number; proposedStart?: string; unavailableReason?: string }>; scheduleDataAvailable: boolean }>(
    dialog === "assign" && ticket?.cwId ? `/api/v1/dispatch/recommend/${ticket.cwId}` : null,
  );
  const rankByTech = new Map((recommend.data?.candidates ?? []).map(c => [c.techId, c]));

  const notes = notesData?.items ?? [];
  // The worker appends a note on every sync, so identical AI lines repeat.
  const displayNotes = React.useMemo(() => collapseDuplicateNotes(notes), [notes]);

  function closeDialog() {
    setDialog(null);
    setStatusId(null);
    setTechId(null);
    setTimeHours("");
    setTimeNotes("");
  }

  function friendlyError(e: unknown): string {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes("HTTP 403")) return "Your role doesn't have permission for this action.";
    if (msg.includes("HTTP 401")) return "Your session expired — log out and back in.";
    return msg.replace(/^HTTP \d+$/, "Request failed — try again.");
  }

  async function runAction(fn: () => Promise<void>) {
    if (!id) return;
    setActionBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setActionBusy(false);
    }
  }

  function selectedSlot(): AppointmentSlot | null {
    const tech = technicians.data?.items.find((t) => t.id === techId);
    const scheduled = scheduling.data?.technicians?.find((t) => t.technicianId === techId)
      ?.slots[0];
    if (scheduled) return { ...scheduled, durationMinutes: scheduling.data?.durationMinutes ?? 60 };
    return tech?.availability?.suggestedSlot ?? tech?.suggestedSlot ?? null;
  }

  async function submitNote() {
    if (!noteText.trim() || !id) return;
    setSubmitting(true);
    try {
      await apiPost(`/api/v1/tickets/${id}/notes`, {
        content: noteText,
        internal: noteInternal,
        ...(!noteInternal ? {
          sendToRecipient: noteToRecipient,
          sendToResources: noteToResources,
          additionalEmails: noteToEmails.split(",").map((s) => s.trim()).filter(Boolean).join(","),
          sendSmsToContact: noteSms,
        } : {}),
      });
      toast.success(noteInternal ? "Internal note added" : "External note added");
      setNoteText("");
      closeDialog();
      refetchNotes();
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  }

  function changeStatus() {
    if (statusId == null) return;
    runAction(async () => {
      await apiPost(`/api/v1/tickets/${id}/status`, { statusId });
      const name = statuses.data?.items.find((s) => s.id === statusId)?.name ?? "new status";
      toast.success(`Status changed to ${name}`);
      closeDialog();
      refetchTicket();
      refetchNotes();
    });
  }

  function escalateTicket() {
    if (!id || !escalateReason.trim()) return;
    runAction(async () => {
      await apiPost(`/api/v1/tickets/${id}/escalate`, { reason: escalateReason.trim() });
      if (escalateTechId) {
        await apiPost(`/api/v1/tickets/${id}/assign`, { techId: escalateTechId });
        const name = technicians.data?.items.find((t) => t.id === escalateTechId)?.name ?? "technician";
        toast.success(`Escalated + assigned to ${name}`);
      } else {
        toast.success("Priority bumped — pick a technician to assign");
      }
      closeDialog();
      refetchTicket();
      refetchNotes();
    });
  }

  function assignTech() {
    if (!techId) return;
    runAction(async () => {
      const slot = selectedSlot();
      await apiPost(`/api/v1/tickets/${id}/assign`, { techId, ...(slot ? { slot } : {}) });
      const name = technicians.data?.items.find((t) => t.id === techId)?.name ?? "technician";
      toast.success(
        slot ? `Assigned to ${name}; suggested slot ${formatSlot(slot)}` : `Assigned to ${name}`,
      );
      closeDialog();
      refetchTicket();
      refetchNotes();
    });
  }

  function logTime() {
    let hours = Number(timeHours);
    if (timeStart && timeEnd) {
      const ms = new Date(timeEnd).getTime() - new Date(timeStart).getTime();
      hours = Math.round((ms / 3600000) * 4) / 4;
    }
    if (!Number.isFinite(hours) || hours <= 0 || hours > 24) {
      toast.error("Enter a valid time range (end after start, max 24 hours).");
      return;
    }
    runAction(async () => {
      const res = await apiPost<{ type: "time_entry" | "draft"; hours: number }>(
        `/api/v1/tickets/${id}/time`,
        { hours, notes: timeNotes, billableOption: timeBillable, ...(timeWorkTypeId != null ? { workTypeId: timeWorkTypeId } : {}), ...(timeStart ? { timeStart: new Date(timeStart).toISOString() } : {}) },
      );
      if (res.type === "time_entry") {
        toast.success(`Logged ${formatHours(res.hours)} in ConnectWise`);
      } else {
        toast.info(
          `Board is in shadow mode — saved ${formatHours(res.hours)} as a note draft for review`,
        );
      }
      closeDialog();
      setTimeStart('');
      refetchNotes();
    });
  }

  async function analyzeNow() {
    if (!id) return;
    setAnalyzing(true);
    try {
      await apiPost(`/api/v1/tickets/${id}/analyze`, {});
      await Promise.all([refetchAnalysis(), refetchTriage()]);
      toast.success("Ticket analysis updated");
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setAnalyzing(false);
    }
  }

  function escalate() {
    runAction(async () => {
      const res = await apiPost<{ fromPriority: number; toPriority: number }>(
        `/api/v1/tickets/${id}/escalate`,
        {},
      );
      toast.success(
        `Escalated from P${res.fromPriority} to P${res.toPriority} — internal note added`,
      );
      refetchTicket();
      refetchNotes();
    });
  }

  const WorkModeIcon = WORK_MODE_ICONS[ticket?.workMode ?? "remote"];

  if (ticketLoading) {
    return (
      <div className="p-6 space-y-4">
        <CardSkeleton lines={4} />
        <CardSkeleton lines={6} />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center px-4">
        <div className="text-5xl mb-4">🔍</div>
        <h2 className="text-lg font-semibold text-[hsl(var(--content))]">Ticket not found</h2>
        <button
          onClick={() => navigate("/tickets")}
          className="mt-4 text-sm text-brand-600 hover:underline"
        >
          ← Back to tickets
        </button>
      </div>
    );
  }

  const catResults = (triageResults ?? []).filter((r) => r.kind === "categorization");
  const catLatest = catResults.length > 0 ? catResults[catResults.length - 1] : null;
  const catRec = (catLatest?.output ?? {}) as { type?: string; subtype?: string; item?: string };
  async function applyCatField(field: "type" | "subtype" | "item", value: string) {
    if (!id) return;
    try {
      await apiPatch(`/api/v1/tickets/${id}/categorization`, { [field]: value });
      toast.success("Categorization updated");
      refetchTicket();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed to update categorization"); }
  }
  async function applyCatRec() {
    if (!id || !catLatest) return;
    try {
      await apiPatch(`/api/v1/tickets/${id}/categorization`, { type: catRec.type, subtype: catRec.subtype, item: catRec.item });
      try { await apiPost(`/api/v1/tickets/${id}/triage/${catLatest.id}/review`, { verdict: "accepted" }); } catch { /* best-effort */ }
      toast.success("AI categorization applied");
      refetchTicket();
      refetchTriage();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed to apply recommendation"); }
  }
  const activeTechs = (technicians.data?.items ?? [])
    .filter((t) => t.cwMemberId > 0)
    .sort((a, b) => {
      const ra = rankByTech.get(a.id)?.rank ?? 99;
      const rb = rankByTech.get(b.id)?.rank ?? 99;
      return ra - rb;
    });

  return (
    <div className={part ? "h-full overflow-y-auto p-4 space-y-4" : embeddedId ? "h-full overflow-y-auto px-4 pt-0 pb-4 space-y-2" : "max-w-[90rem] mx-auto px-4 py-6 space-y-6 lg:pr-[26rem]"}>
      {/* Back + prev/next through the list the user came from */}
      {!embeddedId && (
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => navigate("/tickets")}
          className="flex items-center gap-1.5 text-sm text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to tickets
        </button>
        {ticketNav && navIdx >= 0 && (
          <div className="flex items-center gap-2" data-testid="ticket-nav">
            <button
              data-testid="ticket-prev"
              disabled={!prevTicketId}
              title={prevTicketId ? "Previous ticket in the list" : "First ticket in the list"}
              onClick={() => prevTicketId && navigate(`/tickets/${prevTicketId}`)}
              className="flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] px-2.5 py-1 text-xs font-medium text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </button>
            <span
              className="text-xs text-[hsl(var(--content-placeholder))]"
              data-testid="ticket-nav-position"
            >
              {navIdx + 1} of {ticketNav.ids.length}
            </span>
            <button
              data-testid="ticket-next"
              disabled={!nextTicketId}
              title={nextTicketId ? "Next ticket in the list" : "Last ticket in the list"}
              onClick={() => nextTicketId && navigate(`/tickets/${nextTicketId}`)}
              className="flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] px-2.5 py-1 text-xs font-medium text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      )}

      {/* Header card */}
      {part !== "sidebar" && (
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 shadow-sm">
        {(ticket.companyWarnings ?? []).length > 0 && (
          <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 space-y-1">
            {(ticket.companyWarnings ?? []).map((w) => (
              <p key={w} className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4 shrink-0" /> {w}
              </p>
            ))}
          </div>
        )}
        {/* Type/Subtype/Item selectable dropdowns */}
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xl font-bold text-[hsl(var(--content))] font-mono">
                #{ticket.cwId}
              </span>
              <PriorityBadge priority={ticket.priority} />
              <StatusBadge label={ticket.status} />
              <SlaChip risk={ticket.slaRisk} deadline={ticket.slaDeadline} />
              {ticket.sentiment !== undefined && <SentimentChip score={ticket.sentiment} />}
            </div>
            <h1 className="text-xl font-bold text-[hsl(var(--content))] leading-snug">
              {ticket.summary}
            </h1>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <MetaItem icon={Building} label="Company" value={ticket.company} />
          <MetaItem icon={User} label="Contact" value={ticket.contact} />
          <MetaItem icon={User} label="Email" value={ticket.contactEmail ?? "—"} />
          <div>
            <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--content-subtle))]">
              <Phone className="h-3.5 w-3.5 shrink-0" />
              <span>Phone</span>
            </div>
            <select
              value={selectedPhone}
              onChange={(e) => setSelectedPhone(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-2 py-1 text-xs font-medium text-[hsl(var(--content))] outline-none"
            >
              <option value="">{phoneOptions.length === 0 ? "—" : "Select number…"}</option>
              {phoneOptions.map((p, i) => (
                <option key={i} value={p.value ?? ""}>{p.label}: {p.value ?? "—"}</option>
              ))}
            </select>
          </div>
          <MetaItem
            icon={User}
            label="Owner"
            value={ticket.owner ?? "Unassigned"}
            highlight={!ticket.owner}
          />
          <MetaItem icon={WorkModeIcon} label="Mode" value={ticket.workMode ?? "Standard"} />
        </div>

        <div className="mt-4 flex items-center gap-4 flex-wrap text-xs text-[hsl(var(--content-subtle))]">
          <span>
            Board: <strong className="text-[hsl(var(--content))]">{ticket.board}</strong>
          </span>
          {ticket.estimatedHours && (
            <span>
              Est:{" "}
              <strong className="text-[hsl(var(--content))]">
                {formatHours(ticket.estimatedHours)}
              </strong>
            </span>
          )}
          <span>
            Created:{" "}
            <strong className="text-[hsl(var(--content))]">{relativeTime(ticket.createdAt)}</strong>
          </span>
          <span>
            Updated:{" "}
            <strong className="text-[hsl(var(--content))]">{relativeTime(ticket.updatedAt)}</strong>
          </span>
        </div>
      </div>
      )}

      <div className={"grid grid-cols-1 gap-6 " + (part === "sidebar" ? "" : part === "main" ? "lg:grid-cols-1" : embeddedId ? "lg:grid-cols-4" : "lg:grid-cols-3")}>
        {/* Timeline */}
        {part !== "sidebar" && (
        <div className={(embeddedId ? "lg:col-span-3 " : "lg:col-span-2 ") + "space-y-4"}>
          {id && <SimilarTickets ticketId={id} />}
          <h2 className="text-base font-semibold text-[hsl(var(--content))]">Timeline</h2>

          {notesLoading ? (
            Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={2} />)
          ) : (
            <div
              className="space-y-3"
              data-testid="notes-list"
            >
              {notes.length === 0 && (
                <p className="text-sm text-[hsl(var(--content-subtle))]">No notes yet.</p>
              )}
              {displayNotes.map((note) => (
                <NoteItem
                  key={note.id}
                  note={note}
                  duplicateCount={note.duplicateCount}
                  accepted={acceptedSuggestions.has(note.id)}
                  overridden={overriddenSuggestions.has(note.id)}
                  onAccept={() => {
                    if (!note.triageResultId) {
                      setAcceptedSuggestions((prev) => new Set([...prev, note.id]));
                      return;
                    }
                    runAction(async () => {
                      await apiPost(`/api/v1/tickets/${id}/triage/${note.triageResultId}/review`, {
                        verdict: "accepted",
                      });
                      setAcceptedSuggestions((prev) => new Set([...prev, note.id]));
                      toast.success("AI suggestion accepted");
                    });
                  }}
                  onOverride={() => {
                    if (!note.triageResultId) {
                      setOverriddenSuggestions((prev) => new Set([...prev, note.id]));
                      return;
                    }
                    runAction(async () => {
                      await apiPost(`/api/v1/tickets/${id}/triage/${note.triageResultId}/review`, {
                        verdict: "overridden",
                      });
                      setOverriddenSuggestions((prev) => new Set([...prev, note.id]));
                      toast.success("AI suggestion overridden");
                    });
                  }}
                />
              ))}
            </div>
          )}
        </div>

        )}
        {part !== "main" && !dialog && (
        <div className="space-y-4">
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 space-y-3">
            <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Resources</h3>
            {ticket.resources.length === 0 ? (
              <p className="text-sm text-[hsl(var(--content-subtle))]">No resources assigned</p>
            ) : (
              ticket.resources.map((r) => (
                <div key={r} className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-xs font-semibold text-brand-700 dark:text-brand-300">
                    {r
                      .split(" ")
                      .map((w) => w[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <span className="text-sm text-[hsl(var(--content))]">{r}</span>
                </div>
              ))
            )}
          </div>

          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 space-y-3">
            <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Quick actions</h3>
            <div className="space-y-2">
              <div className="space-y-1.5 pb-2 border-b border-[hsl(var(--border-subtle))]">
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Type</span>
                  <select value={typeSel} onChange={(e) => applyCatField("type", e.target.value)}
                    className="mt-0.5 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-2 py-1.5 text-xs font-medium text-[hsl(var(--content))] outline-none">
                    <option value="">—</option>
                    {[...new Set([...(catOptions?.types ?? []), typeSel].filter(Boolean))].sort().map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Subtype</span>
                  <select value={subtypeSel} onChange={(e) => applyCatField("subtype", e.target.value)}
                    className="mt-0.5 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-2 py-1.5 text-xs font-medium text-[hsl(var(--content))] outline-none">
                    <option value="">—</option>
                    {[...new Set([...(catOptions?.subtypes ?? []), subtypeSel].filter(Boolean))].sort().map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Item</span>
                  <select value={itemSel} onChange={(e) => applyCatField("item", e.target.value)}
                    className="mt-0.5 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-2 py-1.5 text-xs font-medium text-[hsl(var(--content))] outline-none">
                    <option value="">—</option>
                    {[...new Set([...(catOptions?.items ?? []), itemSel].filter(Boolean))].sort().map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Change status</span>
                <select
                  value={statusId ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    const sid = Number(v);
                    setStatusId(sid);
                    runAction(async () => {
                      await apiPost("/api/v1/tickets/" + id + "/status", { statusId: sid });
                      const name = statuses.data?.items.find((s) => s.id === sid)?.name ?? "new status";
                      toast.success("Status changed to " + name);
                      refetchTicket();
                      refetchNotes();
                    });
                  }}
                  className="mt-0.5 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-2 py-1.5 text-xs font-medium text-[hsl(var(--content))] outline-none"
                >
                  <option value="">{statuses.loading ? "Loading…" : "Select status…"}</option>
                  {(statuses.data?.items ?? []).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </label>
              <QuickActionButton emoji="📝" label="Add note" onClick={() => { setNoteInternal(true); setNoteToRecipient(false); setNoteToResources(false); setNoteToEmails(""); setNoteSms(false); setDialog("note"); }} />
              <QuickActionButton emoji="⏱" label="Log time" onClick={() => { setTimeStart(toLocalInput(new Date())); setTimeEnd(""); setDialog("time"); }} />
              <QuickActionButton emoji="🔴" label="Escalate" onClick={() => setDialog("escalate")} disabled={ticket?.priority === 1} title={ticket?.priority === 1 ? "Already at highest priority" : undefined} />
              <QuickActionButton emoji="📅" label="Send booking link" onClick={() => { setBookingUrl(null); setBookingError(""); setDialog("booking"); }} />

            </div>
          </div>
        </div>
        )}
      </div>

      {part !== "sidebar" && (
      <div
        className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 shadow-sm"
        data-testid="ticket-ai-analysis"
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-semibold text-[hsl(var(--content))]">
              AI Assistant
            </h2>
            <p className="text-xs text-[hsl(var(--content-subtle))]">
              AI-generated triage, categorization, and recommendations — review before applying.
            </p>
          </div>
          <button
            onClick={analyzeNow}
            disabled={analyzing}
            className="rounded-lg bg-brand-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
          >
            {analyzing ? "Analyzing…" : "Analyze Now"}
          </button>
        </div>
        {analysisLoading || triageLoading ? (
          <p className="text-sm text-[hsl(var(--content-subtle))]">Loading analysis…</p>
        ) : !analysis ? (
          <p className="text-sm text-[hsl(var(--content-subtle))]">
            No analysis yet. Run it now or enable automatic analysis for this board.
          </p>
        ) : (
          <div className="space-y-5">
            {ticket.aiReason && (
              <div className="flex items-start gap-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
                <span className="text-lg mt-0.5" aria-hidden="true">🧠</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-0.5">Triage reason</p>
                  <p className="text-sm text-blue-800 dark:text-blue-200">{ticket.aiReason}</p>
                  {ticket.confidence !== undefined && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Confidence: {(ticket.confidence * 100).toFixed(0)}%</p>
                  )}
                </div>
              </div>
            )}
            <ActionableSuggestions
              ticketId={id!}
              data={suggestionsData}
              loading={suggestionsLoading}
              refetchSuggestions={refetchSuggestions}
              onRefresh={() => Promise.all([refetchAnalysis(), refetchTriage(), refetchTicket()])}
            />
            <TimeDraftsReview
              ticketId={id!}
              onRefresh={() => Promise.all([refetchTicket(), refetchNotes()])}
            />
            <AnalysisRecommendations
              analysis={analysis}
              results={triageResults ?? []}
              ticketId={id!}
              ticket={ticket}
              suggestions={suggestionsData}
              onRefresh={() => Promise.all([refetchAnalysis(), refetchTriage(), refetchTicket()])}
            />
            {/* Issue #86: approved library scripts for this ticket's problem
                class. Additive, self-contained card (renders null when there
                is no match) - kept out of the cards.push chain above so it
                does not collide with the PR #91/#92 next-steps changes. */}
            <TicketScriptLibraryCard ticketId={id!} />
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <h3 className="text-xs font-semibold uppercase text-[hsl(var(--content-subtle))]">
                  Dedicated summary
                </h3>
                <p className="mt-1 text-sm">
                  <strong>Issue:</strong> {analysis.summary?.issue}
                </p>
                {analysis.summary?.resolution && (
                  <p className="mt-1 text-sm">
                    <strong>Resolution:</strong> {analysis.summary.resolution}
                  </p>
                )}
                {(suggestionsData?.evidence?.entities ?? []).length > 0 ? (
                  <div className="mt-2" data-testid="summary-key-entities">
                    <p className="text-xs font-semibold text-[hsl(var(--content-subtle))]">
                      Key entities
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(suggestionsData?.evidence?.entities ?? []).map((e: string) => (
                        <span
                          key={e}
                          className="rounded-full bg-[hsl(var(--surface))] px-2 py-0.5 text-[11px] text-[hsl(var(--content-subtle))] border border-[hsl(var(--border))]"
                        >
                          {e}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-[hsl(var(--content-subtle))]">
                    Key entities: Unavailable — none detected in the ticket summary or notes.
                  </p>
                )}
                <p className="mt-2 text-[10px] text-[hsl(var(--content-placeholder))]">
                  v{analysis.version} · {analysis.trigger} ·{" "}
                  {String(analysis.fingerprint).slice(0, 8)}
                </p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase text-[hsl(var(--content-subtle))]">
                  Similar closed-ticket solutions
                </h3>
                {(analysis.solutionSuggestions ?? []).length ? (
                  <>
                    {(analysis.solutionSuggestions ?? []).map((x: any) => (
                      <div key={x.cwTicketId} className="mt-1 text-sm">
                        <strong>
                          #{x.cwTicketId} {x.title}
                        </strong>
                        <p>{x.evidence?.[0]}</p>
                      </div>
                    ))}
                    <p className="mt-1 text-[10px] text-[hsl(var(--content-placeholder))]">
                      Matched by company and resolution notes (semantic similarity scores are not
                      stored per match).
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">
                    No grounded historical solution evidence found.
                  </p>
                )}
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase text-[hsl(var(--content-subtle))]">
                  Time analysis
                </h3>
                <p className="mt-1 text-sm">
                  <strong>{formatHours(analysis.timeAnalysis?.totalHours ?? 0)}</strong> across{" "}
                  {analysis.timeAnalysis?.entryCount ?? 0} entries
                  <span className="ml-1 text-xs text-[hsl(var(--content-subtle))]">(nearest 15 min)</span>
                </p>
                <p className="text-xs text-[hsl(var(--content-subtle))]">
                  {analysis.timeAnalysis?.attribution === "verified"
                    ? `Verified technician: ${analysis.timeAnalysis.verifiedTechnician}`
                    : "Technician attribution withheld because entries do not match the ticket owner."}
                </p>
                {(suggestionsData?.evidence?.timeBySource ?? []).length > 0 && (
                  <ul className="mt-2 space-y-0.5" data-testid="time-per-source">
                    {(suggestionsData?.evidence?.timeBySource ?? []).map(
                      (s: { label: string; hours: number; count: number }, i: number) => (
                        <li key={i} className="text-xs text-[hsl(var(--content-subtle))]">
                          · {s.label}: <strong>{formatHours(s.hours)}</strong> ({s.count}{" "}
                          {s.count === 1 ? "entry" : "entries"})
                        </li>
                      ),
                    )}
                  </ul>
                )}
                {suggestionsData?.evidence?.latestWorkNote ? (
                  <p
                    className="mt-2 text-xs italic text-[hsl(var(--content-subtle))] border-l-2 border-[hsl(var(--border))] pl-2"
                    data-testid="latest-work-note"
                  >
                    Latest logged work: “{suggestionsData.evidence.latestWorkNote}”
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-[hsl(var(--content-subtle))]">
                    Latest work note: Unavailable — no synced time entry has a note.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* ── Action dialogs ─────────────────────────────────────────────── */}
      {/* In sidebar (right card) mode dialogs render inline below, replacing the card content */}

      {dialog === "note" && (
        <ActionDialog title="Add note" onClose={closeDialog} inline={part === "sidebar"}>
          <label className="block text-xs font-medium text-[hsl(var(--content-subtle))] mb-1">
            Visibility
          </label>
          <select
            value={noteInternal ? "internal" : "external"}
            onChange={(e) => setNoteInternal(e.target.value === "internal")}
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="internal">🔒 Internal</option>
            <option value="external">📧 External</option>
          </select>
          {!noteInternal && (
            <div className="mt-3 space-y-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] p-3">
              <p className="text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">Send external note to</p>
              <label className="flex items-center gap-2 text-sm text-[hsl(var(--content))]">
                <input type="checkbox" checked={noteToRecipient} onChange={(e) => setNoteToRecipient(e.target.checked)} className="rounded border-[hsl(var(--border))]" />
                Ticket recipient
              </label>
              <label className="flex items-center gap-2 text-sm text-[hsl(var(--content))]">
                <input type="checkbox" checked={noteToResources} onChange={(e) => setNoteToResources(e.target.checked)} className="rounded border-[hsl(var(--border))]" />
                Resources
              </label>
              <label className="flex items-center gap-2 text-sm text-[hsl(var(--content))]">
                <input type="checkbox" checked={noteSms} onChange={(e) => setNoteSms(e.target.checked)} className="rounded border-[hsl(var(--border))]" />
                Text (SMS) to contact phone
              </label>
              <div>
                <span className="text-xs text-[hsl(var(--content-subtle))]">Additional email addresses</span>
                <input
                  type="text"
                  value={noteToEmails}
                  onChange={(e) => setNoteToEmails(e.target.value)}
                  placeholder="a@example.com, b@example.com"
                  className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
          )}
          <label
            className="block text-xs font-medium text-[hsl(var(--content-subtle))] mt-3 mb-1"
            htmlFor="note-content"
          >
            Note
          </label>
          <textarea
            id="note-content"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note, update, or action taken…"
            rows={4}
            className="w-full px-3 py-2 text-sm text-[hsl(var(--content))] placeholder:text-[hsl(var(--content-placeholder))] bg-transparent border rounded-lg outline-none resize-none focus:ring-2 focus:ring-brand-500"
          />
          <DialogFooter
            busy={submitting}
            disabled={!noteText.trim()}
            actionLabel="Add Note"
            onAction={submitNote}
          />
        </ActionDialog>
      )}

      {dialog === "status" && (
        <ActionDialog title="Change status" onClose={closeDialog} inline={part === "sidebar"}>
          {statuses.loading ? (
            <div className="py-6 text-center text-sm text-[hsl(var(--content-subtle))]">
              Loading statuses…
            </div>
          ) : statuses.error ? (
            <p className="text-sm text-red-600">
              Couldn't load statuses: {friendlyError(statuses.error)}
            </p>
          ) : (
            <>
              <p className="text-xs text-[hsl(var(--content-subtle))] mb-3">
                Current:{" "}
                <strong className="text-[hsl(var(--content))]">
                  {statuses.data?.current ?? "Unknown"}
                </strong>
                {" · "}Only Assigned/Scheduled statuses on this board can be set here.
              </p>
              {!statuses.data?.writable && statuses.data?.reason && (
                <p className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                  {statuses.data.reason}
                </p>
              )}
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {(statuses.data?.items ?? []).map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setStatusId(s.id)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg text-sm border transition-colors",
                      statusId === s.id
                        ? "border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-[hsl(var(--content))] font-medium"
                        : "border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]",
                    )}
                  >
                    {s.name}
                  </button>
                ))}
                {(statuses.data?.items ?? []).length === 0 && (
                  <p className="text-sm text-[hsl(var(--content-subtle))] py-2">
                    No other settable statuses on this board.
                  </p>
                )}
              </div>
              <DialogFooter
                busy={actionBusy}
                disabled={statusId == null || !statuses.data?.writable}
                actionLabel="Change status"
                onAction={changeStatus}
              />
            </>
          )}
        </ActionDialog>
      )}

      {dialog === "assign" && (
        <ActionDialog title="Assign technician" onClose={closeDialog} inline={part === "sidebar"}>
          {technicians.loading ? (
            <div className="py-6 text-center text-sm text-[hsl(var(--content-subtle))]">
              Loading technicians…
            </div>
          ) : technicians.error ? (
            <p className="text-sm text-red-600">
              Couldn't load technicians: {friendlyError(technicians.error)}
            </p>
          ) : (
            <>
              <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-100">
                Uses actual ConnectWise calendars, company hours, time off, capacity, buffers, and
                on-call rules. Select a technician to review the slot offered; board shadow/write
                policy still applies.
              </div>
              <div
                className="space-y-2 max-h-72 overflow-y-auto"
                role="radiogroup"
                aria-label="Technician availability"
              >
                {activeTechs.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTechId(t.id)}
                    role="radio"
                    aria-checked={techId === t.id}
                    className={cn(
                      "w-full flex items-center gap-2.5 text-left px-3 py-2 rounded-lg text-sm border transition-colors",
                      techId === t.id
                        ? "border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-[hsl(var(--content))] font-medium"
                        : "border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]",
                    )}
                  >
                    <span className="h-7 w-7 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center text-xs font-semibold text-brand-700 dark:text-brand-300 shrink-0">
                      {t.avatarInitials}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate">{t.name}</span>
                        {rankByTech.get(t.id) && (
                          <span className={cn(
                            "text-[10px] font-bold px-1.5 py-0.5 rounded-full",
                            rankByTech.get(t.id)!.score >= 0.8 ? "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400" :
                            rankByTech.get(t.id)!.score >= 0.6 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400" :
                            "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400"
                          )}>
                            #{rankByTech.get(t.id)!.rank} · {Math.round(rankByTech.get(t.id)!.score * 100)}%
                          </span>
                        )}
                      </span>
                      {rankByTech.get(t.id)?.reason && (
                        <span className="block truncate text-[11px] text-[hsl(var(--content-subtle))]">{rankByTech.get(t.id)!.reason}</span>
                      )}
                      <AvailabilitySummary
                        technician={t}
                        scheduling={scheduling.data?.technicians?.find(
                          (x) => x.technicianId === t.id,
                        )}
                        durationMinutes={scheduling.data?.durationMinutes}
                      />
                      <span className="mt-0.5 block text-[11px] text-[hsl(var(--content-placeholder))]">
                        {t.openTickets} open ticket{t.openTickets === 1 ? "" : "s"}
                      </span>
                    </span>
                  </button>
                ))}
                {activeTechs.length === 0 && (
                  <p className="text-sm text-[hsl(var(--content-subtle))] py-2">
                    No technicians linked to a ConnectWise member.
                  </p>
                )}
              </div>
              {techId &&
                (selectedSlot() ? (
                  <div
                    className="mt-4 rounded-xl border border-brand-200 bg-brand-50 p-3 dark:border-brand-800 dark:bg-brand-950/20"
                    aria-live="polite"
                  >
                    <p className="flex items-center gap-2 text-sm font-semibold">
                      <CalendarClock className="h-4 w-4" />
                      Suggested appointment
                    </p>
                    <p className="mt-1 text-sm">{formatSlot(selectedSlot()!)}</p>
                    <p className="mt-1 text-xs text-[hsl(var(--content-subtle))]">
                      {formatHours(selectedSlot()!.durationMinutes / 60)}. Calendar conflicts and
                      configured buffers are excluded.
                    </p>
                  </div>
                ) : (
                  <div
                    className="mt-4 flex gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-100"
                    role="alert"
                  >
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    No verified appointment slot is available. Ownership can still be assigned, but
                    the console will not claim this technician is free.
                  </div>
                ))}
              <DialogFooter
                busy={actionBusy}
                disabled={!techId}
                actionLabel={selectedSlot() ? "Assign with suggested slot" : "Assign without slot"}
                onAction={assignTech}
              />
            </>
          )}
        </ActionDialog>
      )}

      {dialog === "time" && (
        <ActionDialog title="Log time" onClose={closeDialog} inline={part === "sidebar"}>
          <p className="text-xs text-[hsl(var(--content-subtle))] mb-3">
            Creates a ConnectWise time entry (board write policy applies; otherwise saved as a note
            draft).
          </p>
          <label
            className="block text-xs font-medium text-[hsl(var(--content-subtle))] mb-1"
            htmlFor="log-time-type"
          >
            Type
          </label>
          <select
            id="log-time-type"
            value={timeWorkTypeId ?? ""}
            onChange={(e) => setTimeWorkTypeId(e.target.value ? Number(e.target.value) : null)}
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="">{timeTypes.length === 0 ? "Default work type" : "Select work type…"}</option>
            {timeTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <label
            className="block text-xs font-medium text-[hsl(var(--content-subtle))] mt-3 mb-1"
            htmlFor="log-time-start"
          >
            Start time
          </label>
          <div className="flex items-center gap-2">
            <input
              id="log-time-start"
              type="datetime-local"
              value={timeStart}
              onChange={(e) => setTimeStart(e.target.value)}
              className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              type="button"
              onClick={() => setTimeStart(toLocalInput(new Date()))}
              className="shrink-0 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-xs font-medium text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-hover))]"
            >
              Now
            </button>
          </div>
          <label
            className="block text-xs font-medium text-[hsl(var(--content-subtle))] mt-3 mb-1"
            htmlFor="log-time-end"
          >
            End time
          </label>
          <div className="flex items-center gap-2">
            <input
              id="log-time-end"
              type="datetime-local"
              value={timeEnd}
              onChange={(e) => setTimeEnd(e.target.value)}
              className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              type="button"
              onClick={() => {
                let d = new Date();
                if (timeStart) {
                  const s = new Date(timeStart);
                  if (d.getTime() - s.getTime() < 60000) d = new Date(s.getTime() + 60000);
                }
                setTimeEnd(toLocalInput(d));
              }}
              className="shrink-0 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-xs font-medium text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-hover))]"
            >
              Now
            </button>
          </div>
          <p className="mt-1 text-xs text-[hsl(var(--content-subtle))]">
            {timeStart && timeEnd
              ? "Duration: " + formatHours(Math.max(0, Math.round(((new Date(timeEnd).getTime() - new Date(timeStart).getTime()) / 3600000) * 4) / 4))
              : "Pick start and end times — quarter-hour steps, billing minimums handled by ConnectWise."}
          </p>
          <label
            className="block text-xs font-medium text-[hsl(var(--content-subtle))] mt-3 mb-1"
            htmlFor="log-time-notes"
          >
            Notes
          </label>
          <textarea
            id="log-time-notes"
            value={timeNotes}
            onChange={(e) => setTimeNotes(e.target.value)}
            rows={3}
            placeholder="What was worked on…"
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500 resize-none"
          />
          <DialogFooter
            busy={actionBusy}
            disabled={!(timeStart && timeEnd && new Date(timeEnd).getTime() > new Date(timeStart).getTime()) || timeWorkTypeId == null || !timeNotes.trim()}
            actionLabel="Log time"
            onAction={logTime}
          />
        </ActionDialog>
      )}

      {dialog === "booking" && (
        <ActionDialog title="Send booking link" onClose={closeDialog} inline={part === "sidebar"}>
          <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-100">
            Creates a secure, single-use booking link for this ticket. The customer picks a time slot from your ConnectWise calendar — no login required.
          </div>
          {bookingUrl ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-[hsl(var(--content))]">Booking link created:</p>
              <div className="flex items-center gap-2">
                <input readOnly value={bookingUrl} className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none" onClick={(e) => (e.target as HTMLInputElement).select()} />
                <button type="button" onClick={() => { navigator.clipboard.writeText(bookingUrl); toast.success("Copied!"); }} className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700">Copy</button>
              </div>
              <p className="text-xs text-[hsl(var(--content-subtle))]">Expires in 72 hours. Open in incognito to test the customer flow.</p>
              <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="block text-center text-sm text-brand-600 hover:underline">Open booking page →</a>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[hsl(var(--content-subtle))]">Click below to generate a booking link for ticket #{id}.</p>
              {bookingError && <p className="text-sm text-red-600">{bookingError}</p>}
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => {
                  runAction(async () => {
                    try {
                      const typesRes = await fetch("/api/v1/appointment-types").then(r => r.json());
                      const types = typesRes.items ?? [];
                      if (types.length === 0) {
                        setBookingError("No appointment types configured. Create one at Admin → Appointment Types first.");
                        return;
                      }
                      const res = await apiPost<{ token: string; expiresAt: string }>("/api/v1/bookings/links", {
                        cwTicketId: ticket?.cwId ?? Number(id),
                        appointmentTypeId: types[0].id,
                        expiresInHours: 72,
                      });
                      setBookingUrl("https://app.techsysconnect.com/book/" + res.token);
                      toast.success("Booking link created!");
                    } catch (e) {
                      setBookingError(e instanceof Error ? e.message : "Could not create booking link");
                    }
                  });
                }}
                className="w-full rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {actionBusy ? "Creating…" : "Create booking link"}
              </button>
            </div>
          )}
        </ActionDialog>
      )}

      {dialog === "escalate" && (
        <ActionDialog title="Escalate ticket" onClose={closeDialog} inline={part === "sidebar"}>
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-100">
            Bumps priority one level (e.g. P3 → P2, capped at P1) and adds a [Dispatch] internal note. Then pick the escalation target from the ranked candidates below.
          </div>
          {ticket?.priority === 1 ? (
            <p className="text-sm text-red-600 py-4 text-center">Already at highest priority (P1).</p>
          ) : (
            <>
              <label className="block text-xs font-medium text-[hsl(var(--content-subtle))] mb-1" htmlFor="escalate-reason">Reason (required)</label>
              <textarea
                id="escalate-reason"
                value={escalateReason}
                onChange={(e) => setEscalateReason(e.target.value)}
                rows={2}
                placeholder="Why is this ticket being escalated?"
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-red-500 resize-none mb-3"
              />
              <p className="text-xs font-medium text-[hsl(var(--content-subtle))] mb-2">Escalation target (optional — skip to just bump priority)</p>
              {recommend.loading ? (
                <p className="text-sm text-[hsl(var(--content-subtle))] py-2">Loading ranked candidates…</p>
              ) : recommend.error ? (
                <p className="text-sm text-red-600">Could not load candidates ({recommend.error}).</p>
              ) : (recommend.data?.candidates ?? []).length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto" role="radiogroup" aria-label="Escalation target">
                  {(recommend.data?.candidates ?? []).map((c) => (
                    <button
                      key={c.techId}
                      onClick={() => setEscalateTechId(c.techId)}
                      role="radio"
                      aria-checked={escalateTechId === c.techId}
                      className={cn(
                        "w-full flex items-center gap-2.5 text-left px-3 py-2 rounded-lg text-sm border transition-colors",
                        escalateTechId === c.techId
                          ? "border-red-500 bg-red-50 dark:bg-red-950/20 text-[hsl(var(--content))] font-medium"
                          : "border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]",
                      )}
                    >
                      <span className="h-6 w-6 rounded-full bg-[hsl(var(--surface-subtle))] flex items-center justify-center text-xs font-bold text-[hsl(var(--content-subtle))]">{c.rank}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate">{c.techName}</span>
                        <span className="block truncate text-[11px] text-[hsl(var(--content-subtle))]">{c.reason}</span>
                      </span>
                      <span className="text-xs font-bold tabular-nums">{Math.round(c.score * 100)}%</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[hsl(var(--content-subtle))] py-2">No ranked candidates yet — you can still bump priority without assigning.</p>
              )}
              <DialogFooter
                busy={actionBusy}
                disabled={!escalateReason.trim()}
                actionLabel={escalateTechId ? "Escalate + assign" : "Escalate priority"}
                onAction={escalateTicket}
              />
            </>
          )}
        </ActionDialog>
      )}
    </div>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) + "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
}

function formatSlot(slot: AppointmentSlot): string {
  const start = new Date(slot.startsAt);
  const end = new Date(slot.endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "Invalid calendar slot";
  const date = start.toLocaleDateString("en-US", { timeZone: "America/Chicago",
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const from = start.toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" });
  const to = end.toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" });
  return `${date}, ${from}–${to}`;
}

function AvailabilitySummary({
  technician: t,
  scheduling,
  durationMinutes,
}: {
  technician: Technician;
  scheduling?: SchedulingSlots["technicians"][number];
  durationMinutes?: number;
}) {
  const mapped: TechnicianAvailability | undefined = scheduling
    ? {
        state:
          scheduling.state === "off_hours"
            ? "outside_business_hours"
            : scheduling.state === "capacity"
              ? "at_capacity"
              : scheduling.state === "pto" || scheduling.state === "not_on_call"
                ? "unavailable"
                : scheduling.state,
        suggestedSlot: scheduling.slots[0]
          ? { ...scheduling.slots[0], durationMinutes: durationMinutes ?? 60 }
          : null,
      }
    : undefined;
  const a = mapped ?? t.availability;
  const slot = a?.suggestedSlot ?? t.suggestedSlot;
  const time = (value?: string | null) =>
    value
      ? new Date(value).toLocaleString("en-US", { timeZone: "America/Chicago",
          weekday: "short",
          hour: "numeric",
          minute: "2-digit",
        })
      : null;
  let label: string;
  let tone = "text-[hsl(var(--content-subtle))]";
  switch (a?.state) {
    case "available":
      label = a.availableAt
        ? `Available at ${time(a.availableAt)}`
        : slot
          ? `Available at ${time(slot.startsAt)}`
          : "Available now";
      tone = "text-green-700 dark:text-green-400";
      break;
    case "busy":
      label = `Busy until ${time(a.busyUntil ?? a.availableAt) ?? "a slot opens"}`;
      tone = "text-amber-700 dark:text-amber-400";
      break;
    case "outside_business_hours":
      label = slot
        ? `Outside business hours · Available at ${time(slot.startsAt)}`
        : "Outside business hours";
      tone = "text-blue-700 dark:text-blue-400";
      break;
    case "on_call":
      label = slot ? `On call · Available at ${time(slot.startsAt)}` : "On call";
      tone = "text-violet-700 dark:text-violet-400";
      break;
    case "at_capacity":
      label = slot ? `At capacity · Available at ${time(slot.startsAt)}` : "At capacity";
      tone = "text-orange-700 dark:text-orange-400";
      break;
    case "unavailable":
      label = slot ? `Unavailable · Next slot ${time(slot.startsAt)}` : "Unavailable";
      tone = "text-red-700 dark:text-red-400";
      break;
    case "calendar_unknown":
      label = "Calendar availability unavailable";
      tone = "text-amber-700 dark:text-amber-400";
      break;
    default:
      label = t.onCall
        ? "On call · Calendar slot not verified"
        : t.availableSlot
          ? `Available at ${time(t.availableSlot)}`
          : "Calendar slot not verified";
  }
  const explanation =
    a?.calendarExplanation ??
    (a?.calendarStatus === "stale"
      ? "Calendar data is stale; the next sync may change this slot."
      : a?.calendarStatus === "missing"
        ? "No ConnectWise calendar data has synced for this technician."
        : a?.calendarStatus === "error"
          ? "ConnectWise calendar data could not be loaded."
          : undefined);
  return (
    <>
      <span className={`block text-xs font-medium ${tone}`}>{label}</span>
      {explanation && (
        <span className="mt-0.5 block text-[11px] leading-snug text-[hsl(var(--content-placeholder))]">
          {explanation}
        </span>
      )}
    </>
  );
}

/** Compact before → after diff shown INLINE on recommendation cards (Part 1). */
function BeforeAfter({ before, after }: { before?: string | null; after?: string | null }) {
  if (after == null || after === "") return null;
  return (
    <div
      className="mt-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-xs"
      data-testid="before-after"
    >
      {before != null && before !== after ? (
        <>
          <p className="text-[hsl(var(--content-subtle))]">
            <strong>Before:</strong> {before}
          </p>
          <p className="mt-1 text-[hsl(var(--content))]">
            <strong>After:</strong> {after}
          </p>
        </>
      ) : (
        <p className="whitespace-pre-wrap text-[hsl(var(--content))]">
          <strong>Proposed:</strong> {after}
        </p>
      )}
    </div>
  );
}

/** Dispatch score components the engine already computed (Part 2). */
function ScoreComponents({
  scoreDetail,
}: {
  scoreDetail: { skill?: number; availability?: number; workload?: number; familiarity?: number; urgency?: number };
}) {
  const rows: Array<[string, number | undefined]> = [
    ["Skill", scoreDetail.skill],
    ["Availability", scoreDetail.availability],
    ["Workload", scoreDetail.workload],
    ["Familiarity", scoreDetail.familiarity],
    ["Urgency", scoreDetail.urgency],
  ];
  const present = rows.filter(([, v]) => typeof v === "number") as Array<[string, number]>;
  if (present.length === 0) return null;
  return (
    <div className="mt-2" data-testid="score-components">
      <p className="text-xs font-semibold text-[hsl(var(--content-subtle))]">Score components</p>
      <ul className="mt-1 space-y-0.5">
        {present.map(([label, v]) => (
          <li key={label} className="text-xs text-[hsl(var(--content-subtle))]">
            · {label}: {Math.round(v * 100)}%
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatScoreComponents(c: {
  skillScore?: number;
  availabilityScore?: number;
  workloadScore?: number;
  familiarityScore?: number;
}): string | null {
  const parts: string[] = [];
  if (typeof c.skillScore === "number") parts.push("skill " + Math.round(c.skillScore * 100) + "%");
  if (typeof c.availabilityScore === "number")
    parts.push("availability " + Math.round(c.availabilityScore * 100) + "%");
  if (typeof c.workloadScore === "number")
    parts.push("workload " + Math.round(c.workloadScore * 100) + "%");
  if (typeof c.familiarityScore === "number")
    parts.push("familiarity " + Math.round(c.familiarityScore * 100) + "%");
  return parts.length ? " (" + parts.join(", ") + ")" : null;
}

function AnalysisRecommendations({
  analysis,
  results,
  ticketId,
  ticket,
  suggestions,
  onRefresh,
}: {
  analysis: any;
  results: TriageResult[];
  ticketId: string;
  ticket: Ticket;
  suggestions?: SuggestionsData;
  onRefresh: () => Promise<unknown>;
}) {
  const [editing, setEditing] = React.useState<string | null>(null);
  const [editText, setEditText] = React.useState("");
  const [busy, setBusy] = React.useState<string | null>(null);
  const latest = new Map<string, TriageResult>();
  for (const result of results)
    if (result.kind && !latest.has(result.kind)) latest.set(result.kind, result);

  // Part 2: other categorization results the model produced (honest
  // alternates — why the primary won is confidence, nothing fabricated).
  const categoryAlternates = results.filter(
    (r) => r.kind === "categorization" && r.id !== latest.get("categorization")?.id,
  );
  // Part 2: dispatch candidates with the score components the worker stores.
  const candidateScores = suggestions?.evidence?.candidates ?? [];
  const candidateBlock = candidateScores.length > 0 ? (
    <div className="mt-2" data-testid="technician-scores">
      <p className="text-xs font-semibold text-[hsl(var(--content-subtle))]">
        Dispatch candidates
      </p>
      <ul className="mt-1 space-y-0.5">
        {candidateScores.map((c) => {
          const comps = formatScoreComponents(c);
          return (
            <li key={c.technicianId} className="text-xs text-[hsl(var(--content-subtle))]">
              · <strong className="text-[hsl(var(--content))]">{c.technicianName}</strong>
              {c.score != null ? " — " + Math.round(c.score * 100) + "% overall" : ""}
              {comps ?? " — component scores Unavailable (decision stored before this release)"}
              {c.reason ? " — " + c.reason : ""}
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  const cards: Array<{
    key: string;
    title: string;
    content: React.ReactNode;
    result?: TriageResult;
  }> = [];
  const title = latest.get("title_rewrite");
  if (title) {
    const out = title.output ?? {};
    cards.push({
      key: "title",
      title: "Proposed ticket title",
      result: title,
      content: (
        <p className="font-semibold text-[hsl(var(--content))]">
          {String(out.title ?? "No rewritten title")}
        </p>
      ),
    });
  }
  const category = latest.get("categorization");
  if (category) {
    const out = category.output ?? {};
    const categoryPath = [
      out.board ?? category.board,
      out.type ?? category.type,
      out.subtype ?? category.subtype,
      out.item ?? category.item,
    ]
      .filter(Boolean)
      .map(String)
      .join(" / ");
    cards.push({
      key: "category",
      title: "Categorization",
      result: category,
      content: (
        <>
          {categoryPath ? (
            <>
              <BeforeAfter before={ticket.board || null} after={categoryPath} />
              <button
                type="button"
                disabled={busy === (latest.get('categorization')?.id ?? '')}
                onClick={() => {
                  const out = latest.get('categorization')?.output ?? {};
                  apiPatch("/api/v1/tickets/" + ticketId + "/categorization", {
                    board: out.board ?? category.board,
                    type: out.type ?? category.type,
                    subtype: out.subtype ?? category.subtype,
                    item: out.item ?? category.item,
                  }).then(() => {
                    toast.success("Categorization applied");
                    const catResult = latest.get("categorization"); if (catResult) review(catResult, "accepted");
                  }).catch((e) => toast.error(e instanceof Error ? e.message : "Failed"));
                }}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                Accept recommendation
              </button>
            </>
          ) : (
            <p>No category suggested</p>
          )}
          {categoryAlternates.length > 0 ? (
            <div className="mt-2" data-testid="category-alternates">
              <p className="text-xs font-semibold text-[hsl(var(--content-subtle))]">
                Alternates considered
              </p>
              <ul className="mt-1 space-y-0.5">
                {categoryAlternates.slice(0, 3).map((alt) => {
                  const altOut = alt.output ?? {};
                  const altPath = [
                    altOut.board ?? alt.board,
                    altOut.type ?? alt.type,
                    altOut.subtype ?? alt.subtype,
                    altOut.item ?? alt.item,
                  ]
                    .filter(Boolean)
                    .map(String)
                    .join(" / ");
                  return (
                    <li key={alt.id} className="text-xs text-[hsl(var(--content-subtle))]">
                      · {altPath || "Unnamed category"} — primary won on confidence (
                      {Math.round(category.confidence * 100)}% vs{" "}
                      {Math.round(alt.confidence * 100)}%)
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p
              className="mt-2 text-xs text-[hsl(var(--content-subtle))]"
              data-testid="category-alternates-unavailable"
            >
              Alternates: Unavailable — only one categorization run stored
            </p>
          )}
        </>
      ),
    });
  }
  const triage = latest.get("auto_triage");
  if (triage) {
    const out = triage.output ?? {};
    const internal = (out.internalNote ?? {}) as Record<string, unknown>;
    const missing = Array.isArray(out.missingInfo) ? out.missingInfo.map(String) : [];
    const next = Array.isArray(internal.toConfirm) ? internal.toConfirm.map(String) : [];
    cards.push({
      key: "actions",
      title: "Technician recommendation / next steps",
      result: triage,
      content: (
        <>
          <p>{String(internal.summary ?? "Review the ticket evidence before starting work.")}</p>
          {(next.length > 0 || missing.length > 0) && (
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {[...next, ...missing].map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>
          )}
          {out.targetStatus ? (
            <p className="mt-2 text-xs">
              <strong>Target status:</strong> {String(out.targetStatus)}
            </p>
          ) : null}
          {candidateBlock}
        </>
      ),
    });
  } else if (analysis.summary?.nextSteps?.length) {
    cards.push({
      key: "actions",
      title: "Technician recommendation / next steps",
      content: (
        <>
          <ul className="list-disc space-y-1 pl-5">
            {analysis.summary.nextSteps.map((step: string, i: number) => (
              <li key={i}>{step}</li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-[hsl(var(--content-subtle))]">
            {String(analysis.summary.nextStepsReason ?? "Derived from ticket evidence")}
          </p>
          {candidateBlock}
        </>
      ),
    });
  } else {
    // Honest Unavailable state: never fall back to generic filler text.
    cards.push({
      key: "actions",
      title: "Technician recommendation / next steps",
      content: (
        <p className="text-sm text-[hsl(var(--content-subtle))]">
          Unavailable:{" "}
          {String(
            analysis.summary?.nextStepsReason ??
              "no similar resolved tickets, notes or time entries to derive steps from",
          )}
        </p>
      ),
    });
  }

  async function review(result: TriageResult, verdict: "accepted" | "overridden", reason?: string) {
    setBusy(result.id);
    try {
      await apiPost(`/api/v1/tickets/${ticketId}/triage/${result.id}/review`, {
        verdict,
        ...(reason ? { reason } : {}),
      });
      await onRefresh();
      setEditing(null);
      toast.success(verdict === "accepted" ? "Recommendation applied" : "Recommendation rejected");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not review recommendation");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section aria-label="Technician recommendations">
      <div className="mb-2 flex items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Technician recommendations</h3>
          <p className="text-xs text-[hsl(var(--content-subtle))]">
            Review AI changes before they are used.
          </p>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {cards.map((card) => {
          const verdict = card.result?.correction?.verdict;
          return (
            <article
              key={card.key}
              data-testid={`recommendation-card-${card.key}`}
              className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <h4 className="text-xs font-semibold uppercase text-[hsl(var(--content-subtle))]">
                  {card.title}
                </h4>
                {card.result && card.key !== "title" && (
                  <span className="text-[10px] text-[hsl(var(--content-placeholder))]">
                    {Math.round(card.result.confidence * 100)}% confidence
                  </span>
                )}
              </div>
              <div className="mt-2 text-sm">{card.content}</div>
              {card.result?.reason && card.key !== "title" && (
                <p className="mt-2 text-xs text-[hsl(var(--content-subtle))]">
                  <strong>Reasoning:</strong> {card.result.reason}
                </p>
              )}
              {card.result && (
                <div className="mt-3">
                  {editing === card.result.id ? (
                    <div className="space-y-2">
                      <textarea
                        aria-label={`Edit ${card.title}`}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={3}
                        className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-sm"
                        placeholder="Describe the corrected recommendation…"
                      />
                      <div className="flex gap-2">
                        <button
                          className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                          disabled={!editText.trim() || busy === card.result.id}
                          onClick={() => review(card.result!, "overridden", editText.trim())}
                        >
                          Save edit
                        </button>
                        <button className="px-2 text-xs" onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <button
                        disabled={busy === card.result.id || verdict === "accepted"}
                        onClick={() => review(card.result!, "accepted")}
                        className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        Apply
                      </button>
                      {card.key !== "title" && (
                        <button
                          onClick={() => {
                            setEditing(card.result!.id);
                            setEditText(card.result!.reason);
                          }}
                          className="rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-semibold"
                        >
                          Edit
                        </button>
                      )}
                      <button
                        disabled={busy === card.result.id || verdict === "overridden"}
                        onClick={() => review(card.result!, "overridden")}
                        className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50"
                      >
                        Reject
                      </button>
                      {verdict && (
                        <span className="self-center text-xs font-medium text-[hsl(var(--content-subtle))]">
                          {verdict === "accepted" ? "Applied" : "Rejected / edited"}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Part 1: render the FULL recommended content inline on the suggestion card.
 * Uses the payload directly (what Apply will actually send) enriched by the
 * detail block (before/after, score components, alternates, top matches).
 * For unavailable suggestions only the honest reason is shown (already above).
 */
function renderInlineContent(s: SuggestionItemDto): React.ReactNode {
  const d = s.detail;
  switch (s.action) {
    case "update_summary": {
      const after = String(s.payload.summary ?? d?.after ?? "");
      return (
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-xs">
          {d?.before != null && d.before !== after ? (
            <>
              <p className="text-[hsl(var(--content-subtle))]">
                <strong>Before:</strong> {d.before}
              </p>
              <p className="mt-1 text-[hsl(var(--content))]">
                <strong>After:</strong> {after}
              </p>
            </>
          ) : (
            <p className="text-[hsl(var(--content))]">
              <strong>Proposed summary:</strong> {after}
            </p>
          )}
        </div>
      );
    }
    case "internal_note": {
      const content = String(s.payload.content ?? "");
      return (
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-xs">
          <p className="whitespace-pre-wrap text-[hsl(var(--content))]">
            <strong>Proposed note:</strong> {content}
          </p>
          {(d?.topMatches ?? []).length > 0 && (
            <p className="mt-1 text-[hsl(var(--content-subtle))]">
              Top matches: {(d?.topMatches ?? [])
                .map((m) => "#" + m.cwTicketId + " " + m.title)
                .join("; ")}
            </p>
          )}
        </div>
      );
    }
    case "draft_time": {
      const hours = s.payload.hours;
      const notes = String(s.payload.notes ?? "");
      return (
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-xs">
          <p className="text-[hsl(var(--content))]">
            <strong>Proposed time entry:</strong> {formatHours(Number(hours))} — {notes}
          </p>
        </div>
      );
    }
    case "assign": {
      const name = String(s.payload.technicianName ?? "");
      return (
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-xs">
          <p className="text-[hsl(var(--content))]">
            <strong>Proposed assignment:</strong> {name}
          </p>
          {d?.scoreDetail && <ScoreComponents scoreDetail={d.scoreDetail} />}
          {(d?.alternates ?? []).length > 0 ? (
            <p className="mt-1 text-[hsl(var(--content-subtle))]">
              Alternates considered: {(d?.alternates ?? [])
                .map((a) => a.label + " (" + a.why + ")")
                .join("; ")}
            </p>
          ) : (
            <p className="mt-1 text-[hsl(var(--content-subtle))]">
              Runner-ups: Unavailable — only one candidate evaluated
            </p>
          )}
        </div>
      );
    }
    case "set_status": {
      const statusName = String(s.payload.statusName ?? "");
      return (
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-xs">
          <p className="text-[hsl(var(--content))]">
            <strong>Proposed status:</strong> {statusName}
          </p>
        </div>
      );
    }
    case "checklist": {
      const list = (s.payload.items as string[]) ?? [];
      return (
        <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-xs">
          <p className="text-[hsl(var(--content))]">
            <strong>Proposed checklist note:</strong>
          </p>
          <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-[hsl(var(--content))]">
            {list.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ol>
        </div>
      );
    }
    default:
      return null;
  }
}

function ActionableSuggestions({
  ticketId,
  data,
  loading,
  refetchSuggestions,
  onRefresh,
}: {
  ticketId: string;
  data?: SuggestionsData;
  loading: boolean;
  refetchSuggestions: () => void;
  onRefresh: () => Promise<unknown>;
}) {
  const [editing, setEditing] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [busy, setBusy] = React.useState<string | null>(null);

  // A ticket that was never analysed renders as a wall of "Unavailable".
  // Compute the analysis on first open instead of making someone find the
  // "Analyze" button — this is what makes the panel useful for old tickets.
  const autoAnalyzed = React.useRef(false);
  React.useEffect(() => {
    if (loading || !data || autoAnalyzed.current) return;
    const list = data.items ?? [];
    if (list.length === 0 || list.some((s) => s.available)) return;
    autoAnalyzed.current = true;
    void (async () => {
      try {
        await apiPost(`/api/v1/tickets/${ticketId}/analyze`, {});
        window.setTimeout(() => refetchSuggestions(), 2500);
      } catch {
        // The panel already explains what is missing.
      }
    })();
  }, [loading, data, ticketId, refetchSuggestions]);

  if (loading) {
    return <p className="text-sm text-[hsl(var(--content-subtle))]">Loading suggestions…</p>;
  }
  const items = data?.items ?? [];
  // Only real, grounded actions are rendered as cards. Sections without
  // evidence are listed once underneath with the honest reason — six dead
  // "Unavailable" cards were worse than no cards at all.
  const actionable = items.filter((s) => s.available);
  const unavailable = items.filter((s) => !s.available);
  const outcomes = data?.outcomes ?? [];
  const latestOutcome = new Map<string, (typeof outcomes)[number]>();
  for (const o of outcomes) if (!latestOutcome.has(o.suggestionKey)) latestOutcome.set(o.suggestionKey, o);

  async function apply(key: string, edits?: Record<string, unknown>) {
    setBusy(key);
    try {
      const res = await apiPost<{ ok: boolean; result: Record<string, unknown> }>(
        `/api/v1/tickets/${ticketId}/suggestions/${key}/apply`,
        edits ?? {},
      );
      const drafted = String((res?.result as { type?: string } | undefined)?.type ?? "") === "draft";
      toast.success(
        drafted
          ? "Applied as a draft (board is in shadow mode or ConnectWise is offline)."
          : "Suggestion applied.",
      );
      await Promise.all([refetchSuggestions(), onRefresh()]);
      setEditing(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not apply suggestion");
    } finally {
      setBusy(null);
    }
  }

  async function reject(key: string) {
    setBusy(key);
    try {
      const reason = window.prompt("Why is this suggestion wrong? (optional, saved as feedback)") ?? undefined;
      await apiPost(`/api/v1/tickets/${ticketId}/suggestions/${key}/reject`, reason ? { reason } : {});
      toast.success("Suggestion rejected.");
      await Promise.all([refetchSuggestions(), onRefresh()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not reject suggestion");
    } finally {
      setBusy(null);
    }
  }

  function editTarget(s: (typeof items)[number]): string {
    switch (s.action) {
      case "update_summary":
        return String(s.payload.summary ?? "");
      case "internal_note":
        return String(s.payload.content ?? "");
      case "customer_note":
        return String(s.payload.content ?? "");
      case "draft_time":
        return String(s.payload.hours ?? "");
      case "checklist":
        return ((s.payload.items as string[]) ?? []).join("\n");
      default:
        return s.reason;
    }
  }

  function submitEdit(s: (typeof items)[number]) {
    const v = editValue.trim();
    if (!v) return;
    let edits: Record<string, unknown>;
    switch (s.action) {
      case "update_summary":
        edits = { summary: v };
        break;
      case "internal_note":
        edits = { content: v };
        break;
      case "customer_note":
        edits = { content: v };
        break;
      case "draft_time": {
        const hours = Number(v);
        if (!Number.isFinite(hours) || hours <= 0) {
          toast.error("Hours must be a positive number");
          return;
        }
        edits = { hours };
        break;
      }
      case "checklist":
        edits = { items: v.split("\n").map((x) => x.trim()).filter(Boolean) };
        break;
      default:
        toast.error("This suggestion cannot be edited; apply or reject it.");
        return;
    }
    void apply(s.key, edits);
  }

  return (
    <section aria-label="Actionable AI suggestions">
      <div className="mb-2 flex items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Actionable suggestions</h3>
          <p className="text-xs text-[hsl(var(--content-subtle))]">
            One click applies the real action (note, time draft, assign, status, summary).
          </p>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {actionable.map((s) => {
          const outcome = latestOutcome.get(s.key);
          return (
            <article
              key={s.key}
              data-testid={`suggestion-${s.key}`}
              className={cn(
                "rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] p-4",
                !s.available && "opacity-70",
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <h4 className="text-xs font-semibold uppercase text-[hsl(var(--content-subtle))]">{s.label}</h4>
                {s.available ? (
                  <span className="text-[10px] text-[hsl(var(--content-placeholder))]">
                    {Math.round(s.confidence * 100)}% confidence
                  </span>
                ) : (
                  <span className="rounded bg-[hsl(var(--surface))] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[hsl(var(--content-subtle))]">
                    Unavailable
                  </span>
                )}
              </div>
              <p className="mt-2 text-xs text-[hsl(var(--content-subtle))]">{s.reason}</p>
              {/* Part 1: the full recommended content is ALWAYS visible on the
                  card — the actual proposed text/values, before → after for
                  edits, scores, matches and alternates. No content hides
                  behind the Edit button. */}
              {s.available ? (
                <div className="mt-2" data-testid="suggestion-inline-content">
                  {renderInlineContent(s)}
                </div>
              ) : null}
              {s.computedAt && (
                <p className="mt-1 text-[10px] text-[hsl(var(--content-placeholder))]">
                  Computed {new Date(s.computedAt).toLocaleString("en-US", { timeZone: "America/Chicago" })}
                </p>
              )}
              <div className="mt-3">
                {editing === s.key ? (
                  <div className="space-y-2">
                    <textarea
                      aria-label={`Edit suggestion ${s.key}`}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-2 text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                        disabled={!editValue.trim() || busy === s.key}
                        onClick={() => submitEdit(s)}
                      >
                        Apply edit
                      </button>
                      <button className="px-2 text-xs" onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      disabled={!s.available || busy === s.key || outcome?.status === "applied" || outcome?.status === "edited"}
                      onClick={() => void apply(s.key)}
                      className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      Apply
                    </button>
                    <button
                      disabled={!s.available || busy === s.key}
                      onClick={() => {
                        setEditing(s.key);
                        setEditValue(editTarget(s));
                      }}
                      className="rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                    >
                      Edit
                    </button>
                    <button
                      disabled={busy === s.key || outcome?.status === "rejected"}
                      onClick={() => void reject(s.key)}
                      className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50"
                    >
                      Reject
                    </button>
                    {outcome && (
                      <span className="text-xs font-medium text-[hsl(var(--content-subtle))]">
                        {outcome.status === "applied"
                          ? "Applied"
                          : outcome.status === "edited"
                            ? "Applied (edited)"
                            : "Rejected"}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
      {actionable.length === 0 && (
        <p className="text-sm text-[hsl(var(--content-subtle))]">
          Nothing is safely actionable yet — see what is missing below.
        </p>
      )}
      {unavailable.length > 0 && (
        <details className="mt-3 text-xs text-[hsl(var(--content-subtle))]">
          <summary className="cursor-pointer select-none">
            What is missing ({unavailable.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {unavailable.map((s) => (
              <li key={s.key}>
                <span className="font-semibold text-[hsl(var(--content))]">{s.label}:</span>{" "}
                {s.reason.replace(/^Unavailable:\s*/i, "")}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
function QuickActionButton({
  emoji,
  label,
  onClick,
  disabled,
  title,
}: {
  emoji: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm border transition-colors",
        disabled
          ? "text-[hsl(var(--content-placeholder))] border-[hsl(var(--border))] cursor-not-allowed opacity-60"
          : "text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] hover:text-[hsl(var(--content))] border-[hsl(var(--border))]",
      )}
    >
      <span aria-hidden="true">{emoji}</span>
      <span className="flex-1">{label}</span>
      <ChevronDown className="h-3.5 w-3.5 opacity-40" />
    </button>
  );
}

function ActionDialog({
  title,
  onClose,
  children,
  inline,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  inline?: boolean;
}) {
  return (
    <>
      
      <div
        className={inline
          ? "h-full overflow-y-auto bg-[hsl(var(--surface))]"
          : "fixed right-0 top-14 bottom-0 z-40 w-[24rem] max-w-[92vw] border-l border-t border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-2xl overflow-y-auto"}
        role="dialog"
        aria-label={title}
      >
        <div className="sticky top-0 z-10 bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))] px-5 py-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">
          {children}
        </div>
      </div>
    </>
  );
}

function DialogFooter({
  busy,
  disabled,
  actionLabel,
  onAction,
}: {
  busy: boolean;
  disabled?: boolean;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-end gap-2">
      <button
        onClick={onAction}
        disabled={busy || disabled}
        className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <RefreshCw className={cn("h-3 w-3", busy && "animate-spin")} />
        {busy ? "Working…" : actionLabel}
      </button>
    </div>
  );
}

function MetaItem({
  icon: Icon,
  label,
  value,
  highlight,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-[hsl(var(--content-subtle))] mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-[hsl(var(--content-placeholder))]">{label}</p>
        <p
          className={cn(
            "text-sm font-medium",
            highlight ? "text-amber-600 dark:text-amber-400" : "text-[hsl(var(--content))]",
          )}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function NoteItem({
  note,
  duplicateCount = 1,
  accepted,
  overridden,
  onAccept,
  onOverride,
}: {
  note: TicketNote;
  duplicateCount?: number;
  accepted: boolean;
  overridden: boolean;
  onAccept: () => void;
  onOverride: () => void;
}) {
  const isAi = note.type === "ai_suggestion";
  const icons: Record<string, string> = {
    note: "📝",
    time_entry: "⏱",
    call: "📞",
    status_change: "🔄",
    dispatch: "📋",
    ai_suggestion: "🧠",
  };

  return (
    <div
      className={cn(
        "rounded-xl border p-4 transition-all",
        isAi
          ? "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10"
          : note.type === "status_change"
            ? "border-dashed border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))]"
            : "border-[hsl(var(--border))] bg-[hsl(var(--surface))]",
      )}
    >
      <div className="flex items-start gap-3">
        <span className="text-lg mt-0.5 shrink-0" aria-hidden="true">
          {icons[note.type] ?? "📝"}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-semibold text-[hsl(var(--content))]">{note.author}</span>
            {duplicateCount > 1 && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-[hsl(var(--surface-hover))] text-[hsl(var(--content-subtle))] font-medium"
                title={`${duplicateCount} identical AI runs collapsed into one card`}
              >
                ×{duplicateCount}
              </span>
            )}
            {note.internal && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
                Internal
              </span>
            )}
            {note.timeHours !== undefined && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-medium">
                {formatHours(note.timeHours)}
              </span>
            )}
            {note.draft && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 font-medium">
                Draft (shadow mode)
              </span>
            )}
            <span className="ml-auto text-xs text-[hsl(var(--content-placeholder))]">
              {relativeTime(note.createdAt)}
            </span>
          </div>
          {/* CW notes/emails contain quoted chains, signatures and safelinks —
              NoteContent cleans them for reading and keeps the full email toggle */}
          {note.titleBefore || note.titleAfter || note.categoryBefore || note.categoryAfter ? (
            <div className="space-y-1 text-sm">
              {(note.titleBefore ?? note.categoryBefore) && (
                <p className="text-[hsl(var(--content-subtle))] line-through">
                  {note.titleBefore ?? note.categoryBefore}
                </p>
              )}
              {(note.titleAfter ?? note.categoryAfter) && (
                <p className="font-medium text-[hsl(var(--content))]">
                  {note.titleAfter ?? note.categoryAfter}
                </p>
              )}
            </div>
          ) : (
            <NoteContent content={note.content} />
          )}

          {/* AI suggestion controls */}
          {isAi && !accepted && !overridden && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {note.aiConfidence !== undefined && (
                <span className="text-xs text-blue-700 dark:text-blue-300">
                  Confidence: {(note.aiConfidence * 100).toFixed(0)}%
                </span>
              )}
              {note.aiReason && (
                <span className="text-xs text-[hsl(var(--content-subtle))] italic truncate flex-1">
                  Why: {note.aiReason}
                </span>
              )}
              <button
                onClick={onAccept}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors"
              >
                <CheckCircle2 className="h-3 w-3" /> Accept
              </button>
              <button
                onClick={onOverride}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] text-xs hover:bg-[hsl(var(--surface-subtle))] transition-colors"
              >
                <X className="h-3 w-3" /> Override
              </button>
            </div>
          )}

          {accepted && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-3.5 w-3.5" /> Accepted
            </div>
          )}
          {overridden && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-[hsl(var(--content-subtle))]">
              <X className="h-3.5 w-3.5" /> Overridden
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
